import { MatchesCouponWithQuery as Component } from '@cx-modules/matches-coupon';
import MatchesCouponCss from '@cx-modules/matches-coupon/style.css?inline';

import {
  fetchCampaignData,
  widgetBootstrap
} from '../../services/cx-module-helper';
import themes from './themes';
import themeContract from './themes/contract';
import { meta } from './meta.js';

export const attributes = meta.attributes;
export const widgetName = meta.name;

const {
  VITE_CLICKOUT_SERVICE_URL: clickoutServiceUrl,
  VITE_CX_API_HOST: dataApiEndpoint
} = import.meta.env;

const defaultBetIds = (categoryId?: number) => {
  switch (categoryId) {
    case 23: /*Baseball*/ {
      return [
        1055802107 /* Total Runs */, 1 /* Win Market */, 525 /* Point Spread */
      ];
    }
    case 21: /* Basketball */
    case 25: /* American Football (NFL) */ {
      return [
        525 /* Point Spread */, 526 /* Total Points */, 1 /* Win Market */
      ];
    }
    case 71: /* Hockey */ {
      return [
        1 /* Win Market */, 9 /* Total Goals Over/Under */,
        525 /* Point Spread */
      ];
    }
    default: {
      return [1 /* Win Market */];
    }
  }
};

type Bookmaker = { bookmakerName?: string };

type ContractParams = {
  affIds?: Record<string, string>;
  analytics?: { getDataLayer: () => unknown } | false;
  apiKey?: unknown;
  bookmakers?: Record<string, Bookmaker>;
  campaignName?: unknown;
  categoryId?: number;
  clickoutRedirectTime?: unknown;
  clickoutTemplate?: unknown;
  countryCode?: string;
  eventId?: unknown;
  eventName?: string;
  eventUrl?: string;
  betTypeIds?: number[];
  hash?: unknown;
  oddsFormat?: unknown;
  repub?: string;
  showBookieLogos?: unknown;
  showEventCta?: unknown;
  showEventHeader?: unknown;
  showMoreOddsButton?: unknown;
  showTeamLogos?: unknown;
  subdivisionCode?: string;
  limit?: unknown;
  trackingRate?: number;
};

type ContractProps = {
  apiKey?: unknown;
  baseUrl: string;
  bookmakersConfig: Record<string, Bookmaker>;
  bettypeIds: number[];
  countryCode: string;
  dataApiEndpoint?: string;
  eventId: number;
  eventName: string;
  eventUrl: string;
  overrideBookies?: string[];
  oddsFormat?: unknown;
  onOddsButtonClick: (
    action: unknown,
    betId: unknown,
    bookieCode: string
  ) => void;
  repub?: string;
  requestUrl: string;
  subdivision: string;
  subeventLimit: unknown;
  disableMatchLinks: boolean;
  showBookieLogos: unknown;
  showEventCta: unknown;
  showEventHeader: unknown;
  showMoreOddsButton: unknown;
  showTeamLogos: unknown;
  theme: string;
  disableAnalytics: boolean;
  analyticsImpressionEventData: unknown;
  analyticsImpressionEventName: string;
  analyticsDataLayer?: unknown;
};

export const contract = async ({
  affIds,
  analytics,
  apiKey,
  bookmakers: bookmakersConfig = {},
  campaignName,
  categoryId,
  clickoutRedirectTime,
  clickoutTemplate,
  countryCode,
  eventId,
  eventName,
  eventUrl,
  betTypeIds,
  hash: widgetSlot,
  oddsFormat,
  repub,
  showBookieLogos,
  showEventCta,
  showEventHeader,
  showMoreOddsButton,
  showTeamLogos,
  subdivisionCode,
  limit: subeventLimit
}: ContractParams) => {
  // Derived once from the analytics service's own decision: calling
  // isAnalyticsDisabled(trackingRate) again would re-roll its Math.random()
  // sampling and could disagree with whether `analytics` was created.
  const disableAnalytics = !analytics;

  const bookieCodes = Object.keys(bookmakersConfig);

  const analyticsImpressionEventData = {
    campaignName,
    widgetType: 'matches-coupon',
    variation: 'matches-coupon',
    widgetSlot
  };

  const onOddsButtonClick = (
    action: unknown,
    betId: unknown,
    bookieCode: string
  ) => {
    const pathParams = new URLSearchParams();

    pathParams.append('affId', affIds?.[bookieCode] ?? '');
    pathParams.append('analytics', disableAnalytics ? 'off' : 'on');
    pathParams.append('bookieCode', bookieCode);
    pathParams.append('redirectInterval', String(clickoutRedirectTime));
    pathParams.append('template', String(clickoutTemplate));
    pathParams.append('apiKey', String(apiKey));
    pathParams.append('betIds', String(betId));

    const redirectUrl = `${clickoutServiceUrl}?${pathParams.toString()}`;

    window.open(redirectUrl, '_blank', 'noopener noreferrer');
  };

  const props: ContractProps = {
    apiKey,
    baseUrl: window.location.origin,
    bookmakersConfig,
    bettypeIds: betTypeIds || defaultBetIds(categoryId),
    countryCode: countryCode || 'GB',
    dataApiEndpoint,
    eventId: Number.parseInt(String(eventId)),
    eventName: eventName || '',
    eventUrl: eventUrl || window.location.href,
    overrideBookies: bookieCodes.length ? bookieCodes : undefined,
    oddsFormat,
    onOddsButtonClick,
    repub,
    requestUrl: window.location.href,
    subdivision: subdivisionCode && repub === 'US' ? subdivisionCode : '',
    subeventLimit: subeventLimit || 10,
    disableMatchLinks: true,
    showBookieLogos: showBookieLogos ?? false,
    showEventCta: showEventCta ?? true,
    showEventHeader: showEventHeader ?? false,
    showMoreOddsButton: showMoreOddsButton ?? false,
    showTeamLogos: showTeamLogos ?? false,
    theme: 'widget',
    disableAnalytics,
    analyticsImpressionEventData,
    analyticsImpressionEventName: 'widget_impression'
  };

  if (analytics) {
    props.analyticsDataLayer = analytics.getDataLayer();
  }
  const countriesRequiringSubdivisionCode = ['US', 'CA'];
  const normalisedCountryCode = props.countryCode?.toUpperCase();

  if (
    normalisedCountryCode &&
    countriesRequiringSubdivisionCode.includes(normalisedCountryCode)
  ) {
    props.subdivision = subdivisionCode ?? 'NJ';
  }

  return props;
};

const queryKey = ({
  bookmakers,
  categoryId,
  countryCode,
  subdivision,
  betTypeIds,
  limit: subeventLimit,
  eventId,
  repub
}: {
  bookmakers?: Record<string, Bookmaker>;
  categoryId?: number;
  countryCode?: string;
  subdivision?: string;
  betTypeIds?: number[];
  limit?: unknown;
  eventId?: unknown;
  repub?: string;
}) => [
  'matches-coupon',
  repub?.toUpperCase(),
  countryCode?.toUpperCase(),
  subdivision?.toUpperCase() ?? '',
  eventId?.toString(),
  (betTypeIds || defaultBetIds(categoryId))?.sort((a, b) => a - b).join(','),
  subeventLimit?.toString(),
  Object.keys(bookmakers || {})
    .sort()
    .join(',')
];

const fetchCampaign = (
  attr: HTMLElement & {
    slotId?: string;
    overrideUrl?: string;
    eventId?: unknown;
    sport?: string;
    competition?: string;
    participant?: string;
  }
) => {
  const contextualConfig = {
    ocEventId: attr.eventId,
    sport: attr.sport,
    competition: attr.competition,
    participant: attr.participant
  };

  return fetchCampaignData(
    attr.slotId,
    attr.overrideUrl,
    contextualConfig,
    attr
  );
};

widgetBootstrap({
  attributes,
  contract,
  Component,
  fetchCampaign,
  style: MatchesCouponCss,
  name: widgetName,
  themes,
  themeContract,
  queryKey,
  version: '0.3.0'
});
